import { warn } from '@execonline-inc/logging';
import { saveAs } from 'file-saver';
import { fromEmpty } from 'maybeasy';
import { parse, unparse } from 'papaparse';
import * as R from 'ramda';
import { Task } from 'taskarian';
import { DataParseError } from '../../components/People/ImportParticipants/Store/Types';

interface HasResult extends EventTarget {
  result: ArrayBuffer;
}

const fixdata = (data: ArrayBuffer) => {
  let o = '';
  let l = 0;
  const w = 10240;
  for (; l < data.byteLength / w; ++l) {
    new Uint8Array(data.slice(l * w, l * w + w)).forEach((byte) => {
      if (byte !== 13) {
        o += String.fromCharCode(byte);
      }
    });
  }
  new Uint8Array(data.slice(l * w)).forEach((byte) => {
    o += String.fromCharCode(byte);
  });
  return o.trim();
};

const parseCSVData = (data: string): Task<DataParseError, unknown> =>
  new Task((reject, resolve) => {
    const json = parse(data, { header: true, delimiter: ',', skipEmptyLines: true, newline: '\n' });

    fromEmpty(json.data)
      .map((data) => resolve(data))
      .getOrElse(() =>
        reject({ kind: 'verify', message: 'Source file was empty or in the wrong format' }),
      );

    return () => {
      warn('Parsing csv cannot be cancelled');
    };
  });

const formatFile = (fileEvent: HasResult): Task<DataParseError, string> =>
  new Task((reject, resolve) => {
    try {
      const formattedData = fixdata(fileEvent.result);
      resolve(formattedData);
    } catch (e) {
      warn(e);
      reject({ kind: 'verify', message: 'There was an error formatting the CSV file' });
    }
    return () => {
      warn('Parsing csv cannot be cancelled');
    };
  });

const matchesCSVExtenstion = R.test(/\.csv$/);

const verifyFileType = (file: File): Task<DataParseError, File> =>
  new Task((reject, resolve) => {
    if (file) {
      const fileName = file.name;
      matchesCSVExtenstion(fileName)
        ? resolve(file)
        : reject({ kind: 'verify', message: 'Only use CSV. Could not process this file' });
    } else {
      reject({ kind: 'verify', message: 'Only use CSV files.' });
    }
    return () => {
      warn('Action cannot be cancelled');
    };
  });

const hasResult = (fr: unknown): fr is HasResult => {
  return fr instanceof FileReader && fr.result instanceof ArrayBuffer;
};

const readFile = (file: File): Task<DataParseError, HasResult> =>
  new Task((_, resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (hasResult(reader)) {
        resolve(reader);
      }
    };
    reader.readAsArrayBuffer(file);
    return () => {
      warn('Action cannot be cancelled');
    };
  });

export const csvToJson = (file: File) =>
  verifyFileType(file).andThen(readFile).andThen(formatFile).andThen(parseCSVData);

export const exportJsonToCSV = (data: {}[], fileName: string) => {
  const csv = unparse(data);
  return saveAs(new Blob([csv], { type: 'application/octet-stream' }), fileName);
};
